/**
 * Recurrence Operations — generation engine, completion hooks, pause/resume/skip.
 *
 * Core ops for the recurring tasks system. Used by:
 * - Client: after-completion generation in completeTask() flow
 * - Client: fallback fixed-schedule generation on app-start (7-day lookahead)
 * - CF DO: primary fixed-schedule generation (30-day lookahead, daily cron)
 *
 * All generation is idempotent — deterministic IDs prevent duplicates.
 */

import dayjs from "dayjs"
import utc from "dayjs/plugin/utc.js"
import timezone from "dayjs/plugin/timezone.js"
import * as Y from "yjs"
import type { DbRecurrence, DbObject } from "./db-types.js"
import { ObjectType, ObjectStatus, StartAtType, StartAtSubType } from "./db-types.js"
import { getDb, getNow, emitEvent, getOpsTimeZoneOrLookup } from "./runtime.js"
import { newFileId } from "./fileOps.js"
import { addLocalDays } from "./dayTime.js"
import { createTaskDefaults } from "./utils.js"
import { getInsertIndex } from "./helpers/indexHelpers.js"
import { resolveContainerRealmId } from "./helpers/realmHelpers.js"
import {
  deterministicInstanceId,
  computeAfterCompletionKey,
  expandRruleOccurrences,
  computeNextAfterCompletionDate,
  RECURRENCE_SKIPPED,
} from "./recurrenceHelpers.js"
import type { OpResult } from "./op-types.js"
import { ok, fail } from "./op-types.js"

dayjs.extend(utc)
dayjs.extend(timezone)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Apply per-weekday time override to a scheduled timestamp.
 * weekdayTimes is stored as JSON: Record<number, string> where key is 0=Mon..6=Sun.
 */
function applyWeekdayTime(scheduledMs: number, recurrence: DbRecurrence): number {
  if (!recurrence.weekdayTimes) return scheduledMs
  let weekdayMap: Record<string, string>
  try {
    weekdayMap = JSON.parse(recurrence.weekdayTimes)
  } catch {
    return scheduledMs
  }
  // Convert JS day (0=Sun) to Monday-based (0=Mon)
  const d = dayjs(scheduledMs).tz(recurrence.timezone)
  const mondayBased = (d.day() + 6) % 7
  const timeStr = weekdayMap[String(mondayBased)]
  if (!timeStr) return scheduledMs
  const [h, m] = timeStr.split(":").map(Number)
  if (h == null || m == null) return scheduledMs
  return d.hour(h).minute(m).second(0).millisecond(0).valueOf()
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GenerateResult {
  created: number
  skipped: number
  instanceIds: string[]
}

// ---------------------------------------------------------------------------
// §4: Generation Engine — FIXED mode
// ---------------------------------------------------------------------------

/**
 * Generate recurring instances for a FIXED-schedule template.
 * Expands RRULE within [now, now + lookaheadDays], creates instances idempotently.
 *
 * @param templateId The recurrence/template task ID
 * @param overrideLookahead Override lookahead days (default: recurrence.lookaheadDays)
 * @returns Result with count of created/skipped instances
 */
export async function generateFixedInstances(
  templateId: string,
  overrideLookahead?: number,
): Promise<OpResult<GenerateResult>> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")
  if (recurrence.mode !== "FIXED") return fail("Not a FIXED recurrence")
  if (recurrence.pausedAt) return ok({ created: 0, skipped: 0, instanceIds: [] })
  if (recurrence.isDeleted) return ok({ created: 0, skipped: 0, instanceIds: [] })
  if (!recurrence.rrule) return fail("FIXED recurrence has no rrule")

  const templateTask = await db.totodo.get(templateId)
  if (!templateTask || templateTask.isDeleted) return fail("Template task not found or deleted")

  const now = dayjs.utc().valueOf()
  const lookahead = overrideLookahead ?? recurrence.lookaheadDays
  // Use start-of-today in recurrence timezone as lower bound to include today's occurrence
  const from = dayjs().tz(recurrence.timezone).startOf("day").utc().valueOf()
  const to = dayjs(now).add(lookahead, "day").valueOf()

  // Build full RRULE string — rrule may already include DTSTART line
  const rruleStr = recurrence.rrule
  const hasDtstart = rruleStr.includes("DTSTART")
  const fullRrule = hasDtstart
    ? rruleStr
    : `DTSTART:${dayjs(recurrence.startAt).utc().format("YYYYMMDDTHHmmss[Z]")}\nRRULE:${rruleStr}`

  // Expand RRULE occurrences (from start-of-today to include today's occurrence)
  const occurrences = expandRruleOccurrences(fullRrule, recurrence.timezone, from, to)

  let created = 0
  let skipped = 0
  const instanceIds: string[] = []

  for (const occ of occurrences) {
    // Check end conditions
    if (recurrence.endDate && occ.utcMs > Number(recurrence.endDate)) break
    if (recurrence.maxCount && (recurrence.instanceCount + created) >= recurrence.maxCount) break

    // Exclude dates check (ISO date string match)
    if (recurrence.excludeDates?.length) {
      const occDate = dayjs(occ.utcMs).tz(recurrence.timezone).format("YYYY-MM-DD")
      if (recurrence.excludeDates.includes(occDate)) {
        skipped++
        continue
      }
    }

    const instanceId = deterministicInstanceId(templateId, occ.occurrenceKey)

    // Dedup check — does this instance already exist?
    const existing = await db.totodo.get(instanceId)
    if (existing) {
      skipped++
      continue
    }

    // Pile-up check
    const shouldGenerate = await checkPilePolicy(recurrence, created)
    if (!shouldGenerate) {
      skipped++
      continue
    }

    // Create instance (not into a closed or trashed project / list)
    const made = await createInstance(db, recurrence, templateTask, instanceId, occ.occurrenceKey, applyWeekdayTime(occ.utcMs, recurrence))
    if (!made) {
      skipped++
      continue
    }
    instanceIds.push(instanceId)
    created++
  }

  // Update recurrence state
  if (created > 0) {
    await db.recurrence.update(templateId, {
      lastGeneratedAt: String(dayjs.utc().valueOf()),
      instanceCount: recurrence.instanceCount + created,
    })
  }

  return ok({ created, skipped, instanceIds })
}

// ---------------------------------------------------------------------------
// §6: After-Completion generation
// ---------------------------------------------------------------------------

/**
 * Generate the next instance after an AFTER_COMPLETION task is completed/skipped.
 * Called from completeRecurringTask() or skipRecurrenceInstance().
 *
 * @param templateId The template task ID
 * @param completedAtMs When completion occurred (UTC ms)
 */
export async function generateNextAfterCompletion(
  templateId: string,
  completedAtMs: number,
): Promise<OpResult<{ instanceId: string } | null>> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")
  if (recurrence.mode !== "AFTER_COMPLETION") return fail("Not AFTER_COMPLETION mode")
  if (recurrence.pausedAt) return ok(null)
  if (recurrence.isDeleted) return ok(null)

  const templateTask = await db.totodo.get(templateId)
  if (!templateTask || templateTask.isDeleted) return fail("Template task not found")

  // Check end conditions
  if (recurrence.maxCount && recurrence.instanceCount >= recurrence.maxCount) return ok(null)

  const nextDateMs = computeNextAfterCompletionDate(
    recurrence.afterCompletionDays ?? 1,
    recurrence.afterCompletionTime,
    completedAtMs,
    recurrence.timezone,
  )

  if (recurrence.endDate && nextDateMs > Number(recurrence.endDate)) return ok(null)

  // Pile-up check
  const shouldGenerate = await checkPilePolicy(recurrence, 0)
  if (!shouldGenerate) return ok(null)

  const seqNumber = recurrence.instanceCount + 1
  const occurrenceKey = computeAfterCompletionKey(seqNumber)
  const instanceId = deterministicInstanceId(templateId, occurrenceKey)

  // Dedup check
  const existing = await db.totodo.get(instanceId)
  if (existing) return ok({ instanceId })

  const made = await createInstance(db, recurrence, templateTask, instanceId, occurrenceKey, nextDateMs)
  if (!made) return ok(null) // its project / list is closed or trashed: no instance

  await db.recurrence.update(templateId, {
    instanceCount: seqNumber,
    lastGeneratedAt: String(dayjs.utc().valueOf()),
  })

  return ok({ instanceId })
}

// ---------------------------------------------------------------------------
// §6: Complete recurring task hook
// ---------------------------------------------------------------------------

/**
 * Handle completion of a recurring task instance.
 * Updates streak, and for AFTER_COMPLETION mode, generates next.
 */
export async function completeRecurringInstance(taskId: string): Promise<void> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task?.recurringTemplateId) return

  // Set instanceOutcome for history/streak tracking
  await db.totodo.update(taskId, { instanceOutcome: "completed" })

  const recurrence = await db.recurrence.get(task.recurringTemplateId)
  if (!recurrence) return

  // Auto-focus: unfocus completed instance to free the slot for the next one
  if (recurrence.autoFocus === 1) {
    const { unfocusTask } = await import("./focusOps.js")
    await unfocusTask(taskId).catch(() => {})
  }

  // Update streak (§13)
  await updateStreakOnCompletion(task.recurringTemplateId)

  // Progressive overload: advance target
  if (recurrence.progressiveField) {
    await advanceProgressiveTarget(task.recurringTemplateId)
  }

  // Flexible window: increment counter + generate next if below goal
  if (recurrence.flexibleCount && recurrence.flexiblePeriod) {
    await handleFlexibleCompletion(recurrence)
    return
  }

  // For AFTER_COMPLETION, generate next
  if (recurrence.mode === "AFTER_COMPLETION") {
    const completedAtMs = dayjs.utc().valueOf()
    await generateNextAfterCompletion(task.recurringTemplateId, completedAtMs)
  }

  // For FIXED mode with autoFocus: focus the next open instance (since instances are pre-generated)
  if (recurrence.mode === "FIXED" && recurrence.autoFocus === 1) {
    const nextOpen = await db.totodo
      .where("recurringTemplateId")
      .equals(task.recurringTemplateId)
      .filter((t) => t.status === ObjectStatus.Unfinished && !t.isDeleted && t.id !== taskId)
      .sortBy("createdAt")
    if (nextOpen.length > 0) {
      const { focusTask } = await import("./focusOps.js")
      await focusTask(nextOpen[0]!.id!).catch(() => {})
    }
  }
}

// ---------------------------------------------------------------------------
// §9: Skip, Pause & Resume
// ---------------------------------------------------------------------------

/**
 * Skip a recurring instance — marks as canceled with userStatus="skipped".
 * For AFTER_COMPLETION: triggers next generation (skip counts as "done").
 */
export async function skipRecurrenceInstance(instanceId: string, reason?: string): Promise<OpResult> {
  const db = getDb()
  const task = await db.totodo.get(instanceId)
  if (!task) return fail("Task not found")
  if (!task.recurringTemplateId) return fail("Not a recurring instance")

  const now = getNow()
  await db.totodo.update(instanceId, {
    status: ObjectStatus.Canceled,
    statusAt: now,
    userStatus: RECURRENCE_SKIPPED,
    instanceOutcome: "skipped",
    skipReason: reason,
    updatedAt: now,
  })

  emitEvent("recurrence_skipped", { taskId: instanceId, templateId: task.recurringTemplateId })

  // For AFTER_COMPLETION: skip triggers next generation
  const recurrence = await db.recurrence.get(task.recurringTemplateId)
  if (recurrence?.mode === "AFTER_COMPLETION") {
    await generateNextAfterCompletion(task.recurringTemplateId, Number(now))
  }

  return ok()
}

/**
 * Pause a recurrence template — no new instances will be generated.
 * Optional: reason (e.g. "vacation") and resumeAt for auto-resume.
 */
export async function pauseRecurrence(
  templateId: string,
  opts?: { reason?: string; resumeAt?: string },
): Promise<OpResult> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  await db.recurrence.update(templateId, {
    pausedAt: String(dayjs.utc().valueOf()),
    pauseReason: opts?.reason,
    resumeAt: opts?.resumeAt,
    updatedAt: String(dayjs.utc().valueOf()),
  })

  emitEvent("recurrence_paused", { templateId })
  return ok()
}

/**
 * Resume a paused recurrence. Resets startAt to now (don't generate past instances).
 */
export async function resumeRecurrence(templateId: string): Promise<OpResult> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  const now = String(dayjs.utc().valueOf())
  await db.recurrence.update(templateId, {
    pausedAt: undefined,
    pauseReason: undefined,
    resumeAt: undefined,
    startAt: now,
    updatedAt: now,
  })

  emitEvent("recurrence_resumed", { templateId })
  return ok()
}

/**
 * Add an exclude date (EXDATE) to a recurrence.
 * Instances on this date will not be generated.
 * @param date ISO date string, e.g. "2025-12-25"
 */
export async function addExcludeDate(templateId: string, date: string): Promise<OpResult> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  const existing = recurrence.excludeDates ?? []
  if (existing.includes(date)) return ok() // Already excluded

  await db.recurrence.update(templateId, {
    excludeDates: [...existing, date],
    updatedAt: String(dayjs.utc().valueOf()),
  })
  return ok()
}

/**
 * Remove an exclude date from a recurrence.
 */
export async function removeExcludeDate(templateId: string, date: string): Promise<OpResult> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  const existing = recurrence.excludeDates ?? []
  if (!existing.includes(date)) return ok()

  await db.recurrence.update(templateId, {
    excludeDates: existing.filter((d) => d !== date),
    updatedAt: String(dayjs.utc().valueOf()),
  })
  return ok()
}

// ---------------------------------------------------------------------------
// §4: Create/Update/Delete Recurrence
// ---------------------------------------------------------------------------

export interface CreateRecurrenceOpts {
  templateId: string
  mode: "FIXED" | "AFTER_COMPLETION"
  timezone: string
  startAt: string
  rrule?: string
  afterCompletionDays?: number
  afterCompletionTime?: string
  weekdayTimes?: string // JSON: Record<number, string>
  pilePolicy?: "ALLOW" | "NO_PILE" | "CAP"
  pileCap?: number
  endDate?: string
  maxCount?: number
  deadlineOffset?: string
  showInTodayDaysBefore?: number
  lookaheadDays?: number
  reminderOffsetMs?: number
  reminderKind?: "reminder" | "alarm"
  reminderOffsets?: Array<{ offsetMs: number; kind: "reminder" | "alarm" }>
  insertPosition?: "top" | "bottom"
  targetContainerId?: string
  // Flexible window (goal-based)
  flexibleCount?: number
  flexiblePeriod?: "week" | "month"
  flexibleResetDay?: number
  // Exclude dates
  excludeDates?: string[]
  // Progressive overload
  progressiveField?: string
  progressiveStart?: number
  progressiveIncrement?: number
  progressiveUnit?: string
  progressiveMax?: number
  progressivePer?: "instance" | "week" | "month"
  // Focus
  autoFocus?: 0 | 1
  // AI Enrichment
  aiEnabled?: 0 | 1
  aiPrompt?: string
  aiCreativity?: string
  aiAvoidRepeat?: 0 | 1
  aiDetailLevel?: string
}

/**
 * Create a new recurrence config for a template task.
 * Marks the template task with isRecurringTemplate=1.
 */
export async function createRecurrence(opts: CreateRecurrenceOpts): Promise<OpResult<{ id: string }>> {
  const db = getDb()
  const now = getNow()

  // Verify template task exists
  const task = await db.totodo.get(opts.templateId)
  if (!task) return fail("Template task not found")

  const recurrence: DbRecurrence = {
    id: opts.templateId,
    rrule: opts.rrule,
    timezone: opts.timezone,
    mode: opts.mode,
    startAt: opts.startAt,
    version: 1,
    afterCompletionDays: opts.afterCompletionDays,
    afterCompletionTime: opts.afterCompletionTime,
    weekdayTimes: opts.weekdayTimes,
    pilePolicy: opts.pilePolicy ?? "NO_PILE",
    pileCap: opts.pileCap,
    endDate: opts.endDate,
    maxCount: opts.maxCount,
    deadlineOffset: opts.deadlineOffset,
    showInTodayDaysBefore: opts.showInTodayDaysBefore,
    lookaheadDays: opts.lookaheadDays ?? 30,
    reminderOffsetMs: opts.reminderOffsetMs,
    reminderKind: opts.reminderKind,
    reminderOffsets: opts.reminderOffsets ? JSON.stringify(opts.reminderOffsets) : undefined,
    insertPosition: opts.insertPosition,
    targetContainerId: opts.targetContainerId,
    flexibleCount: opts.flexibleCount,
    flexiblePeriod: opts.flexiblePeriod,
    flexibleResetDay: opts.flexibleResetDay,
    flexibleCompletedThisPeriod: opts.flexibleCount ? 0 : undefined,
    flexiblePeriodStart: opts.flexibleCount ? now : undefined,
    excludeDates: opts.excludeDates,
    progressiveField: opts.progressiveField,
    progressiveStart: opts.progressiveStart,
    progressiveIncrement: opts.progressiveIncrement,
    progressiveUnit: opts.progressiveUnit,
    progressiveMax: opts.progressiveMax,
    progressivePer: opts.progressivePer,
    progressiveCurrent: opts.progressiveStart,
    autoFocus: opts.autoFocus,
    aiEnabled: opts.aiEnabled,
    aiPrompt: opts.aiPrompt,
    lastGeneratedAt: undefined,
    pausedAt: undefined,
    instanceCount: 0,
    currentStreak: 0,
    longestStreak: 0,
    owner: task.owner,
    realmId: task.realmId,
    isDeleted: 0,
    createdAt: now,
    createdBy: db.cloud.currentUserId,
  }

  await db.recurrence.add(recurrence)

  // Mark template task
  await db.totodo.update(opts.templateId, {
    isRecurringTemplate: 1,
    updatedAt: now,
  })

  // For AFTER_COMPLETION: generate first instance immediately
  // so user has an active task to complete (template is hidden from date views)
  if (opts.mode === "AFTER_COMPLETION") {
    const startAtEntry = await db.startAtIndex.get(`#${opts.templateId}`)
    const scheduledAtMs = startAtEntry ? Number(startAtEntry.startAt) : Number(now)

    const seqNumber = 1
    const occurrenceKey = computeAfterCompletionKey(seqNumber)
    const instanceId = deterministicInstanceId(opts.templateId, occurrenceKey)

    const made = await createInstance(db, recurrence, task, instanceId, occurrenceKey, scheduledAtMs)
    if (made) {
      await db.recurrence.update(opts.templateId, {
        instanceCount: seqNumber,
        lastGeneratedAt: String(dayjs.utc().valueOf()),
      })
    }
  }

  emitEvent("recurrence_created", { templateId: opts.templateId, mode: opts.mode })
  return ok({ id: opts.templateId })
}

/**
 * Update an existing recurrence config. Bumps version to detect stale instances.
 */
export async function updateRecurrence(
  templateId: string,
  updates: Partial<Omit<DbRecurrence, "id" | "createdAt" | "createdBy" | "owner" | "realmId">>,
): Promise<OpResult> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  await db.recurrence.update(templateId, {
    ...updates,
    version: recurrence.version + 1,
    updatedAt: getNow(),
    updatedBy: db.cloud.currentUserId,
  })

  emitEvent("recurrence_updated", { templateId })
  return ok()
}

/**
 * Delete a recurrence (soft-delete). Template task keeps isRecurringTemplate=1
 * but no new instances will be generated. Existing instances remain.
 */
export async function deleteRecurrence(
  templateId: string,
  opts?: { deleteFutureInstances?: boolean },
): Promise<OpResult> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  const now = getNow()
  await db.recurrence.update(templateId, {
    isDeleted: 1,
    updatedAt: now,
    updatedBy: db.cloud.currentUserId,
  })

  // Clear template flag
  await db.totodo.update(templateId, {
    isRecurringTemplate: 0,
    updatedAt: now,
  })

  // Optionally delete/cancel future unfinished instances
  if (opts?.deleteFutureInstances) {
    await deleteFutureInstances(templateId)
  }

  emitEvent("recurrence_deleted", { templateId })
  return ok()
}

// ---------------------------------------------------------------------------
// §4: Generate all (batch — for DO cron)
// ---------------------------------------------------------------------------

/**
 * Generate instances for ALL active FIXED recurrences.
 * Used by CF DO cron (daily 03:00 UTC).
 */
export async function generateAllFixedInstances(
  overrideLookahead?: number,
): Promise<GenerateResult> {
  const db = getDb()
  const recurrences = await db.recurrence
    .where("mode")
    .equals("FIXED")
    .filter(r => !r.pausedAt && !r.isDeleted)
    .toArray()

  let totalCreated = 0
  let totalSkipped = 0
  const allInstanceIds: string[] = []

  for (const recurrence of recurrences) {
    const result = await generateFixedInstances(recurrence.id, overrideLookahead)
    if (result.ok && result.data) {
      totalCreated += result.data.created
      totalSkipped += result.data.skipped
      allInstanceIds.push(...result.data.instanceIds)
    }
  }

  return { created: totalCreated, skipped: totalSkipped, instanceIds: allInstanceIds }
}

// ---------------------------------------------------------------------------
// §4b: Delete future unfinished instances (for regeneration)
// ---------------------------------------------------------------------------

/**
 * Delete all future unfinished instances of a recurrence template,
 * including their reminders and startAtIndex entries.
 *
 * After calling this, `generateFixedInstances` will recreate them
 * with correct times. Works on synced tables — propagates to all devices.
 *
 * Use cases:
 *  - User changes recurrence time/schedule
 *  - Bug fix: instances created with wrong data
 */
export async function deleteFutureInstances(templateId: string): Promise<number> {
  const db = getDb()
  const now = getNow()

  const instances = await db.totodo
    .where("recurringTemplateId")
    .equals(templateId)
    .filter(t => t.status === ObjectStatus.Unfinished && !t.isDeleted)
    .toArray()

  if (instances.length === 0) return 0

  for (const inst of instances) {
    // Delete reminders (synced)
    const reminders = await db.reminder
      .where("taskId")
      .equals(inst.id!)
      .filter(r => !r.isDeleted)
      .toArray()
    for (const rem of reminders) {
      await db.reminder.update(rem.id, { isDeleted: 1, deletedAt: now })
    }

    // Delete startAtIndex (local)
    await db.startAtIndex.delete(`#${inst.id}`)

    // Soft-delete the instance (synced)
    await db.totodo.update(inst.id!, {
      status: ObjectStatus.Canceled,
      isDeleted: 1,
      deletedAt: now,
      updatedAt: now,
    })
  }

  // Decrement instanceCount so generation doesn't hit maxCount prematurely
  const recurrence = await db.recurrence.get(templateId)
  if (recurrence) {
    await db.recurrence.update(templateId, {
      instanceCount: Math.max(0, recurrence.instanceCount - instances.length),
    })
  }

  if (instances.length > 0) {
    console.info(`[recurrence] Deleted ${instances.length} future instances for template ${templateId.substring(0, 8)}`)
  }

  return instances.length
}

// ---------------------------------------------------------------------------
// §8: Pile-up Policy check
// ---------------------------------------------------------------------------

/**
 * Check if pile-up policy allows generating a new instance.
 */
async function checkPilePolicy(recurrence: DbRecurrence, pendingCreations: number): Promise<boolean> {
  if (recurrence.pilePolicy === "ALLOW") return true

  const db = getDb()
  const openInstances = await db.totodo
    .where("recurringTemplateId")
    .equals(recurrence.id)
    .filter(t =>
      t.status === ObjectStatus.Unfinished &&
      !t.isDeleted &&
      t.instanceOutcome !== "completed" &&
      t.instanceOutcome !== "skipped",
    )
    .count()

  const effectiveOpen = openInstances + pendingCreations

  if (recurrence.pilePolicy === "NO_PILE") {
    return effectiveOpen === 0
  }
  if (recurrence.pilePolicy === "CAP") {
    return effectiveOpen < (recurrence.pileCap ?? 3)
  }
  return true
}

// ---------------------------------------------------------------------------
// §10: Deadline computation
// ---------------------------------------------------------------------------

/**
 * Compute deadline from scheduledAtMs + deadlineOffset DSL.
 * Uses simple "+Nd" format. Returns UTC ms or undefined.
 */
function computeDeadline(
  scheduledAtMs: number,
  deadlineOffset: string | undefined,
  timeZone?: string,
): string | undefined {
  if (!deadlineOffset) return undefined

  // Parse simple "+Nd" format (e.g. "+0d", "+10d", "+3d")
  const match = deadlineOffset.match(/^\+(\d+)d$/)
  if (!match) return undefined

  const days = Number(match[1])
  // The deadline is N calendar days on, in the user's zone on the server and the runtime's own in the app
  return String(timeZone ? addLocalDays(scheduledAtMs, days, timeZone) : dayjs(scheduledAtMs).add(days, "day").valueOf())
}

/**
 * Compute progressive overload target for the next instance.
 * Returns the current target value, or undefined if progressive is not configured.
 */
function computeProgressiveTarget(recurrence: DbRecurrence): number | undefined {
  if (!recurrence.progressiveField || recurrence.progressiveStart == null) return undefined

  const current = recurrence.progressiveCurrent ?? recurrence.progressiveStart
  const max = recurrence.progressiveMax ?? Infinity
  return Math.min(current, max)
}

/**
 * Advance progressive overload target after an instance is completed.
 * Called from completeRecurringInstance when progressive is configured.
 *
 * - "instance" mode: increment after every completion
 * - "week"/"month" mode: increment once per period (first completion in period)
 */
export async function advanceProgressiveTarget(templateId: string): Promise<void> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return
  if (!recurrence.progressiveField || recurrence.progressiveIncrement == null) return

  const current = recurrence.progressiveCurrent ?? recurrence.progressiveStart ?? 0
  const max = recurrence.progressiveMax ?? Infinity

  if (current >= max) return // Already at max

  let shouldAdvance = false

  if (recurrence.progressivePer === "instance") {
    shouldAdvance = true
  } else {
    // Week/month mode: advance only once per period
    const now = dayjs.utc()
    const lastAdvanced = recurrence.lastCompletedAt ? dayjs(Number(recurrence.lastCompletedAt)) : null
    if (!lastAdvanced) {
      shouldAdvance = true
    } else {
      const unit = recurrence.progressivePer === "week" ? "week" : "month"
      shouldAdvance = !now.isSame(lastAdvanced, unit)
    }
  }

  if (shouldAdvance) {
    const newTarget = Math.min(current + (recurrence.progressiveIncrement ?? 0), max)
    await db.recurrence.update(templateId, {
      progressiveCurrent: newTarget,
    })
  }
}

// ---------------------------------------------------------------------------
// §13: Streak tracking
// ---------------------------------------------------------------------------

/**
 * Update streak cache on the recurrence row after an instance is completed.
 */
async function updateStreakOnCompletion(templateId: string): Promise<void> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return

  // Get resolved instances sorted by occurrenceKey desc
  const instances = await db.totodo
    .where("recurringTemplateId")
    .equals(templateId)
    .filter(t => !t.isDeleted && (t.status === ObjectStatus.Completed || t.status === ObjectStatus.Canceled))
    .toArray()

  // Sort by occurrenceKey descending (most recent first)
  instances.sort((a, b) => (b.occurrenceKey ?? "").localeCompare(a.occurrenceKey ?? ""))

  // Calculate current streak:
  // - completed → +1
  // - skipped (instanceOutcome === "skipped") → continue (doesn't break)
  // - anything else (expired, deferred, canceled without outcome) → break
  let streak = 0
  for (const inst of instances) {
    if (inst.instanceOutcome === "skipped") continue // Skip doesn't break streak
    if (inst.status === ObjectStatus.Completed) {
      streak++
    } else {
      break
    }
  }

  const lastCompleted = instances.find(i => i.status === ObjectStatus.Completed)

  await db.recurrence.update(templateId, {
    currentStreak: streak,
    longestStreak: Math.max(streak, recurrence.longestStreak ?? 0),
    lastCompletedAt: lastCompleted?.statusAt,
  })
}

// ---------------------------------------------------------------------------
// Instance creation (internal)
// ---------------------------------------------------------------------------

/** What cloneTaskContent could not copy (it never throws: the task itself is already made). */
export interface ContentCloneProblem {
  part: "files" | "document"
  error: string
}

/** Regex to find totodo-file:// URIs in HTML strings */
const FILE_URI_RE = /totodo-file:\/\/([^"'>\s\\]+)/g

/**
 * Clone all files attached to a source task, returning an old→new ID map.
 * New files get targetKey as parentId, and `realmId` when given (see cloneTaskContent).
 * Uses deterministic IDs (based on occurrenceKey) to prevent duplicates
 * when client + server both generate the same recurring instance.
 */
async function cloneFiles(
  db: import("./db-types.js").TotodoDb,
  sourceKey: string,
  targetKey: string,
  occurrenceKey?: string,
  realmId?: string,
  problems?: ContentCloneProblem[],
): Promise<Map<string, string>> {
  const fileMap = new Map<string, string>()
  let total = 0
  let copied = 0
  try {
    const files = await db.files.where("parentId").equals(sourceKey).toArray()
    total = files.length
    if (files.length === 0) return fileMap

    for (const file of files) {
      // Dexie Cloud refuses a files id without its "fls" prefix: plain UUIDs here made every copy of a
      // file owned by the task (parentId - API attachments) fail. The editor's own images carry no
      // parentId, are not copied here, and the copy's document keeps pointing at the originals.
      const newId = newFileId(occurrenceKey ? deterministicInstanceId(file.id!, occurrenceKey) : undefined)
      const existing = occurrenceKey ? await db.files.get(newId) : undefined
      if (!existing) {
        await db.files.add({
          ...file,
          id: newId,
          parentId: targetKey,
          ...(realmId !== undefined ? { realmId } : {}),
          createdAt: getNow(),
        })
      }
      // Mapped only once the copy exists: an unmapped image keeps pointing at the original file.
      fileMap.set(file.id!, newId)
      copied++
    }
  } catch (e) {
    // Non-fatal, but reported to a caller that asked (duplicateTask): the copy is still made.
    problems?.push({
      part: "files",
      error: `${copied} of ${total || "?"} attached files copied: ${e instanceof Error ? e.message : String(e)}`,
    })
  }
  return fileMap
}

/**
 * Rewrite totodo-file:// URIs in a Y.Doc's XML fragment using a file ID map.
 * Walks all XmlText nodes and replaces old file IDs with new ones.
 */
function rewriteFileUrlsInDoc(doc: Y.Doc, fileMap: Map<string, string>): void {
  const fragment = doc.getXmlFragment("default")
  walkXmlFragment(fragment, fileMap)
}

function walkXmlFragment(
  node: Y.XmlFragment | Y.XmlElement,
  fileMap: Map<string, string>,
): void {
  for (let i = 0; i < node.length; i++) {
    const child = node.get(i)
    if (child instanceof Y.XmlElement) {
      // Check attrs for file URIs (e.g. image src)
      const src = child.getAttribute("src")
      if (src && src.startsWith("totodo-file://")) {
        const oldId = src.slice("totodo-file://".length)
        const newId = fileMap.get(oldId)
        if (newId) {
          child.setAttribute("src", `totodo-file://${newId}`)
        }
      }
      // Recurse
      walkXmlFragment(child, fileMap)
    }
  }
}

/**
 * Rewrite totodo-file:// URIs in an HTML string.
 */
function rewriteFileUrlsInHtml(
  html: string,
  fileMap: Map<string, string>,
): string {
  return html.replace(FILE_URI_RE, (_match, oldId: string) => {
    const newId = fileMap.get(oldId)
    return newId ? `totodo-file://${newId}` : `totodo-file://${oldId}`
  })
}

/**
 * Clone task content: Y.Doc + files + URL rewriting.
 * Handles the complete content duplication for recurring instances (and duplicateTask,
 * without an occurrenceKey: fresh file ids).
 * `realmId`: the realm of the task the copies belong to, when it may differ from the source's
 * (a recurring instance placed in a shared container): the copied files go there with it, or
 * whoever the task is shared with gets image links to files they cannot open. Omitted, each
 * copy keeps its source file's realm.
 * `problems`: when given, whatever could not be copied (files, the Y.Doc) is pushed to it; it
 * is still never thrown. A caller that does not pass it (recurring instances) behaves as before.
 * Returns the rewritten docHtml (or original if no files to rewrite).
 */
export async function cloneTaskContent(
  db: import("./db-types.js").TotodoDb,
  sourceKey: string,
  targetKey: string,
  docHtml: string | undefined,
  occurrenceKey?: string,
  realmId?: string,
  problems?: ContentCloneProblem[],
): Promise<string | undefined> {
  // Clone files first — always attempt this
  let fileMap: Map<string, string>
  try {
    fileMap = await cloneFiles(db, sourceKey, targetKey, occurrenceKey, realmId, problems)
  } catch (e) {
    // File cloning failed — return original docHtml
    problems?.push({ part: "files", error: e instanceof Error ? e.message : String(e) })
    return docHtml
  }

  // Try to clone Y.Doc (non-fatal if table doesn't exist)
  try {
    const updatesTable = db.table("$totodo.doc_updates")
    const entries = await updatesTable.where("k").equals(sourceKey).toArray()
    if (entries.length > 0) {
      // Reconstruct doc
      const doc = new Y.Doc()
      for (const entry of entries) {
        Y.applyUpdateV2(doc, entry.u)
      }

      // Rewrite file URLs in the Y.Doc if we have cloned files
      if (fileMap.size > 0) {
        rewriteFileUrlsInDoc(doc, fileMap)
      }

      // Store consolidated update under target key
      const consolidated = Y.encodeStateAsUpdateV2(doc)
      doc.destroy()
      await updatesTable.add({ k: targetKey, u: consolidated, f: 1 })
    }
  } catch (e) {
    // Y.Doc cloning failed — non-fatal, still rewrite docHtml below. A database without the
    // document table has no Y.Doc to copy: that is not a failure.
    if (!(e instanceof Error && e.name === "InvalidTableError")) {
      problems?.push({ part: "document", error: e instanceof Error ? e.message : String(e) })
    }
  }

  // Rewrite docHtml file URLs regardless of Y.Doc success
  if (fileMap.size > 0 && docHtml) {
    return rewriteFileUrlsInHtml(docHtml, fileMap)
  }
  return docHtml
}

/**
 * Clone checklist items from a template task to an instance.
 * Resets all items to Unfinished status. Uses deterministic IDs.
 * The rows go under the instance's own path, which differs from the template's
 * when the instance is placed elsewhere (targetContainerId, project instances),
 * and in the instance's realm, as addChecklistItem puts a task's rows.
 */
async function cloneChecklist(
  db: import("./db-types.js").TotodoDb,
  templateTask: DbObject,
  instance: DbObject,
  occurrenceKey: string,
): Promise<void> {
  try {
    const clPath = `${templateTask.parentPath}/${templateTask.id}`
    const items = await db.totodo
      .where("parentPath")
      .equals(clPath)
      .filter((item: DbObject) => item.isDeleted !== 1 && item.type === ObjectType.Checklist)
      .toArray()

    if (items.length === 0) return

    const instanceClPath = `${instance.parentPath}/${instance.id}`
    const now = getNow()

    for (const item of items) {
      const itemInstanceId = deterministicInstanceId(item.id!, occurrenceKey)
      await db.totodo.add(createTaskDefaults({
        id: itemInstanceId,
        type: ObjectType.Checklist,
        subType: item.subType,
        title: item.title,
        parentPath: instanceClPath,
        index: item.index,
        status: ObjectStatus.Unfinished,
        isDeleted: 0,
        owner: item.owner,
        realmId: instance.realmId,
        createdAt: now,
        createdBy: item.createdBy,
      }))
    }
  } catch {
    // Non-fatal
  }
}

/** Whether `parentPath` is `root/<id>` of a project / list that is completed, canceled or in the trash. */
async function parentContainerIsClosed(db: import("./db-types.js").TotodoDb, parentPath: string): Promise<boolean> {
  const segs = parentPath.split("/")
  if (segs.length !== 2 || segs[0] !== "root" || !segs[1] || segs[1].startsWith("#")) return false
  const container = await db.totodo.get(segs[1])
  if (!container || (container.type !== ObjectType.Project && container.type !== ObjectType.List)) return false
  return container.isDeleted === 1 || container.status !== ObjectStatus.Unfinished
}

/**
 * Create a single recurring instance task.
 * Copies template fields, sets scheduling, creates reminder if configured.
 */
async function createInstance(
  db: import("./db-types.js").TotodoDb,
  recurrence: DbRecurrence,
  templateTask: DbObject,
  instanceId: string,
  occurrenceKey: string,
  scheduledAtMs: number,
): Promise<boolean> {
  const now = getNow()

  // Resolve target container: use targetContainerId if set and container is active
  let effectiveParentPath = templateTask.parentPath
  let realmId = templateTask.realmId
  if (recurrence.targetContainerId) {
    const container = await db.totodo.get(recurrence.targetContainerId)
    if (container && container.status === ObjectStatus.Unfinished && !container.isDeleted) {
      // A container's tasks live under root/<id> (a project in an area too), as the app's
      // new-task defaults and move picker place them
      effectiveParentPath = `root/${recurrence.targetContainerId}`
      // ...in that container's realm, as moveTask gives a task moved there (the owner stays):
      // a private template's instance in a shared project must be shared with it
      realmId = await resolveContainerRealmId(effectiveParentPath)
    }
    // Fallback: if container doesn't exist or is inactive, use template's parentPath
  }

  // No instance into a project / list that is closed or in the trash: the task would be open inside
  // a closed container, hidden from Today and Upcoming (and reopening the project for it would undo
  // the user's close). The occurrence is generated again once the container is open.
  if (await parentContainerIsClosed(db, effectiveParentPath)) return false

  // Calculate insert index
  const insertPos = recurrence.insertPosition ?? "bottom"
  const index = await getInsertIndex(effectiveParentPath, insertPos, ObjectType.Task)

  // Build instance task
  const zone = getOpsTimeZoneOrLookup()
  const timeZone = zone instanceof Promise ? await zone : zone
  const instance = createTaskDefaults({
    id: instanceId,
    type: templateTask.type,
    title: templateTask.title,
    note: templateTask.note,
    parentPath: effectiveParentPath,
    index,
    status: ObjectStatus.Unfinished,
    tags: [...templateTask.tags],
    dueAt: computeDeadline(scheduledAtMs, recurrence.deadlineOffset, timeZone),
    isDeleted: 0,
    owner: templateTask.owner,
    realmId,
    createdAt: now,
    createdBy: templateTask.createdBy,
    subType: templateTask.subType,
    icon: templateTask.icon,
    docHtml: templateTask.docHtml,
    userStatus: templateTask.userStatus,
    // Recurrence fields
    recurringTemplateId: recurrence.id,
    occurrenceKey,
    isRecurrenceInstance: 1,
    // Progressive overload target
    progressiveTarget: computeProgressiveTarget(recurrence),
    // AI enrichment: mark as pending if recurrence has AI enabled
    ...(recurrence.aiEnabled === 1 && recurrence.aiPrompt ? { aiState: "pending" as const } : {}),
  })

  // Write task
  await db.totodo.add(instance)

  // Clone content: Y.Doc + files + rewrite URLs (deterministic file IDs via occurrenceKey).
  // The files go in the instance's realm, as its checklist does.
  const rewrittenHtml = await cloneTaskContent(
    db,
    recurrence.id,
    instanceId,
    templateTask.docHtml,
    occurrenceKey,
    realmId,
  )
  if (rewrittenHtml && rewrittenHtml !== templateTask.docHtml) {
    await db.totodo.update(instanceId, { docHtml: rewrittenHtml })
  }

  // Clone checklist items (reset to unchecked)
  await cloneChecklist(db, templateTask, instance, occurrenceKey)

  // Create startAtIndex entry for scheduling
  const startAtStr = String(scheduledAtMs)
  const lastIdx = await db.startAtIndex.orderBy("index").last()
  const startAtIndex = (lastIdx?.index ?? 0) + 1

  await db.startAtIndex.add({
    id: `#${instanceId}`,
    index: startAtIndex,
    startAt: startAtStr,
    type: StartAtType.Default,
    subType: templateTask.subType === StartAtSubType.ThisEvening
      ? StartAtSubType.ThisEvening
      : StartAtSubType.Default,
  })

  // §10: Create reminders if configured
  // Use deterministic ID to prevent duplicates when client + server both generate
  // the same instance before sync propagates (random UUID would create two entries).
  const offsets: Array<{ offsetMs: number; kind: "reminder" | "alarm" }> = recurrence.reminderOffsets
    ? JSON.parse(recurrence.reminderOffsets)
    : recurrence.reminderOffsetMs != null
      ? [{ offsetMs: recurrence.reminderOffsetMs, kind: recurrence.reminderKind ?? "reminder" }]
      : []

  for (let i = 0; i < offsets.length; i++) {
    const entry = offsets[i]!
    const { offsetMs, kind } = entry
    const reminderFireAt = scheduledAtMs + offsetMs
    const reminderId = i === 0 ? `rem_${instanceId}` : `rem_${instanceId}_${i}`
    const existing = await db.reminder.get(reminderId)
    if (!existing || existing.isDeleted) {
      await db.reminder.put({
        id: reminderId,
        taskId: instanceId,
        parentPath: effectiveParentPath,
        owner: templateTask.owner ?? "",
        realmId: templateTask.realmId,
        title: templateTask.title,
        sourceType: "internal",
        triggerType: "time",
        fireAt: String(reminderFireAt),
        kind: kind,
        createdAt: now,
        createdBy: templateTask.createdBy ?? "",
        isDeleted: 0,
      })
    }
  }

  emitEvent("recurrence_instance_created", { instanceId, templateId: recurrence.id, occurrenceKey })

  // Stamp routine chain fields if this template belongs to a routine
  const { stampRoutineFields } = await import("./routineOps.js")
  await stampRoutineFields(instanceId, recurrence.id)

  // Auto-focus: if enabled, add new instance to Focus (best-effort, respects max limit)
  if (recurrence.autoFocus === 1) {
    const { focusTask } = await import("./focusOps.js")
    await focusTask(instanceId).catch(() => {})
  }
  return true
}

// ---------------------------------------------------------------------------
// §7: Recurring Projects — generate project + children
// ---------------------------------------------------------------------------

/**
 * Generate a recurring project instance (project + sections + tasks).
 * All children get deterministic IDs based on their template ID + occurrenceKey.
 */
export async function generateProjectInstance(
  templateId: string,
  occurrenceKey: string,
  scheduledAtMs: number,
): Promise<OpResult<{ projectId: string; childrenCreated: number }>> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")

  const templateProject = await db.totodo.get(templateId)
  if (!templateProject || templateProject.isDeleted) return fail("Template project not found")

  const now = getNow()
  const projectInstanceId = deterministicInstanceId(templateId, occurrenceKey)

  // Check if project instance already exists (idempotent)
  const existing = await db.totodo.get(projectInstanceId)
  if (existing) return ok({ projectId: projectInstanceId, childrenCreated: 0 })

  // Create project instance
  const projectIndex = await getInsertIndex(templateProject.parentPath, recurrence.insertPosition ?? "bottom", ObjectType.Project)

  const zone = getOpsTimeZoneOrLookup()
  const timeZone = zone instanceof Promise ? await zone : zone
  const projectInstance = createTaskDefaults({
    id: projectInstanceId,
    type: templateProject.type,
    title: templateProject.title,
    note: templateProject.note,
    parentPath: templateProject.parentPath,
    index: projectIndex,
    status: ObjectStatus.Unfinished,
    tags: [...templateProject.tags],
    dueAt: computeDeadline(scheduledAtMs, recurrence.deadlineOffset, timeZone),
    isDeleted: 0,
    owner: templateProject.owner,
    realmId: templateProject.realmId,
    createdAt: now,
    createdBy: templateProject.createdBy,
    subType: templateProject.subType,
    icon: templateProject.icon,
    docHtml: templateProject.docHtml,
    recurringTemplateId: templateId,
    occurrenceKey,
    isRecurrenceInstance: 1,
  })

  await db.totodo.add(projectInstance)

  // Clone content: Y.Doc + files + rewrite URLs
  const projectHtml = await cloneTaskContent(db, templateId, projectInstanceId, templateProject.docHtml, occurrenceKey)
  if (projectHtml && projectHtml !== templateProject.docHtml) {
    await db.totodo.update(projectInstanceId, { docHtml: projectHtml })
  }

  const startAtStr = String(scheduledAtMs)
  const lastIdx = await db.startAtIndex.orderBy("index").last()
  await db.startAtIndex.add({
    id: `#${projectInstanceId}`,
    index: (lastIdx?.index ?? 0) + 1,
    startAt: startAtStr,
    type: StartAtType.Default,
    subType: StartAtSubType.Default,
  })

  // Copy all children (sections + tasks inside the template project). Checklist rows
  // are cloned with their task by cloneChecklist; copied here they would land under
  // the template child's id and take the ids cloneChecklist needs.
  const templateProjectPath = `root/${templateId}`
  const children = await db.totodo
    .where("[parentPath+isDeleted]")
    .between([templateProjectPath, 0], [templateProjectPath + "\uffff", 0])
    .filter(c => !c.isDeleted && c.type !== ObjectType.Checklist)
    .toArray()

  // Sort by index to maintain order
  children.sort((a, b) => a.index - b.index)

  let childrenCreated = 0

  for (const child of children) {
    const childInstanceId = deterministicInstanceId(child.id!, occurrenceKey)

    // Skip if already exists
    const childExisting = await db.totodo.get(childInstanceId)
    if (childExisting) continue

    // Rewrite parentPath: replace template project ID with instance project ID
    const childParentPath = child.parentPath.replace(templateId, projectInstanceId)

    const childInstance = createTaskDefaults({
      id: childInstanceId,
      type: child.type,
      title: child.title,
      note: child.note,
      parentPath: childParentPath,
      index: child.index,
      status: ObjectStatus.Unfinished,
      tags: [...child.tags],
      isDeleted: 0,
      owner: child.owner,
      realmId: child.realmId,
      createdAt: now,
      createdBy: child.createdBy,
      subType: child.subType,
      icon: child.icon,
      docHtml: child.docHtml,
      recurringTemplateId: templateId,
      occurrenceKey,
      isRecurrenceInstance: 1,
    })

    await db.totodo.add(childInstance)

    // Clone content: Y.Doc + files + rewrite URLs
    const childHtml = await cloneTaskContent(db, child.id!, childInstanceId, child.docHtml, occurrenceKey)
    if (childHtml && childHtml !== child.docHtml) {
      await db.totodo.update(childInstanceId, { docHtml: childHtml })
    }

    // Clone checklist items for child tasks
    if (child.type === ObjectType.Task) {
      await cloneChecklist(db, child, childInstance, occurrenceKey)
    }

    childrenCreated++

    // If this is a task (not section) with a due date or reminder, schedule it
    if (child.type === ObjectType.Task && scheduledAtMs) {
      const childLastIdx = await db.startAtIndex.orderBy("index").last()
      await db.startAtIndex.add({
        id: `#${childInstanceId}`,
        index: (childLastIdx?.index ?? 0) + 1,
        startAt: startAtStr,
        type: StartAtType.Default,
        subType: StartAtSubType.Default,
      })
    }
  }

  // Update recurrence state
  await db.recurrence.update(templateId, {
    lastGeneratedAt: String(dayjs.utc().valueOf()),
    instanceCount: recurrence.instanceCount + 1,
  })

  emitEvent("recurrence_project_created", { projectId: projectInstanceId, templateId, childrenCreated: String(childrenCreated) })
  return ok({ projectId: projectInstanceId, childrenCreated })
}

/**
 * Generate instances for a FIXED project recurrence.
 * Expands RRULE and creates project instances for each date.
 */
export async function generateFixedProjectInstances(
  templateId: string,
  overrideLookahead?: number,
): Promise<OpResult<GenerateResult>> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")
  if (recurrence.mode !== "FIXED") return fail("Not a FIXED recurrence")
  if (recurrence.pausedAt || recurrence.isDeleted) return ok({ created: 0, skipped: 0, instanceIds: [] })
  if (!recurrence.rrule) return fail("No rrule defined")

  const templateProject = await db.totodo.get(templateId)
  if (!templateProject || templateProject.isDeleted) return fail("Template project not found")
  if (templateProject.type !== ObjectType.Project && templateProject.type !== ObjectType.List) {
    return fail("Template is not a project or list")
  }

  const now = dayjs.utc().valueOf()
  const lookahead = overrideLookahead ?? recurrence.lookaheadDays
  // Use start-of-today in recurrence timezone as lower bound to include today's occurrence
  const from = dayjs().tz(recurrence.timezone).startOf("day").utc().valueOf()
  const to = dayjs(now).add(lookahead, "day").valueOf()

  // Build full RRULE string — rrule may already include DTSTART line
  const rruleStr = recurrence.rrule
  const hasDtstart = rruleStr.includes("DTSTART")
  const fullRrule = hasDtstart
    ? rruleStr
    : `DTSTART:${dayjs(recurrence.startAt).utc().format("YYYYMMDDTHHmmss[Z]")}\nRRULE:${rruleStr}`
  const occurrences = expandRruleOccurrences(fullRrule, recurrence.timezone, from, to)

  let created = 0
  let skipped = 0
  const instanceIds: string[] = []

  for (const occ of occurrences) {
    if (recurrence.endDate && occ.utcMs > Number(recurrence.endDate)) break
    if (recurrence.maxCount && (recurrence.instanceCount + created) >= recurrence.maxCount) break

    // Pile-up check for projects
    const shouldGenerate = await checkPilePolicy(recurrence, created)
    if (!shouldGenerate) { skipped++; continue }

    const result = await generateProjectInstance(templateId, occ.occurrenceKey, occ.utcMs)
    if (result.ok && result.data) {
      instanceIds.push(result.data.projectId)
      created++
    } else {
      skipped++
    }
  }

  return ok({ created, skipped, instanceIds })
}

// ---------------------------------------------------------------------------
// §13: Logbook — query instances grouped by template
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// §Flexible Window — Goal-based recurrence (e.g. "3× per week")
// ---------------------------------------------------------------------------

/**
 * Handle completion of a flexible window instance.
 * Increments the period counter and generates the next instance if goal not met.
 */
async function handleFlexibleCompletion(recurrence: DbRecurrence): Promise<void> {
  const db = getDb()
  const now = getNow()

  // Check if period needs reset first
  const periodNeedsReset = isFlexiblePeriodExpired(recurrence)
  let completedThisPeriod = recurrence.flexibleCompletedThisPeriod ?? 0

  if (periodNeedsReset) {
    // Period expired — this completion starts a new period
    completedThisPeriod = 1
    await db.recurrence.update(recurrence.id, {
      flexibleCompletedThisPeriod: 1,
      flexiblePeriodStart: now,
      updatedAt: now,
    })
  } else {
    completedThisPeriod += 1
    await db.recurrence.update(recurrence.id, {
      flexibleCompletedThisPeriod: completedThisPeriod,
      updatedAt: now,
    })
  }

  // Generate next instance if goal not yet met
  if (completedThisPeriod < (recurrence.flexibleCount ?? 0)) {
    await generateFlexibleInstance(recurrence.id)
  }
  // If goal met, no new instance — wait for next period
}

/**
 * Generate a single flexible instance for the template.
 * Places it in Today/Anyday (startAt = now, no specific time).
 */
async function generateFlexibleInstance(templateId: string): Promise<void> {
  const db = getDb()
  const now = getNow()

  const template = await db.totodo.get(templateId)
  if (!template) return

  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return

  // Check pile policy
  if (recurrence.pilePolicy === "NO_PILE") {
    const openCount = await db.totodo
      .where("recurringTemplateId")
      .equals(templateId)
      .filter(t => !t.isDeleted && t.status === ObjectStatus.Unfinished)
      .count()
    if (openCount > 0) return
  }

  // Deterministic IDs: use seq:N pattern based on instanceCount to prevent
  // duplicates if multiple sources trigger generation for the same sequence.
  const nextSeq = (recurrence.instanceCount || 0) + 1
  const occurrenceKey = `flex-seq:${nextSeq}`
  const instanceId = deterministicInstanceId(templateId, occurrenceKey)

  // Dedup: if this instance already exists (another source generated it), skip
  const existing = await db.totodo.get(instanceId)
  if (existing) return

  await db.totodo.add({
    ...template,
    id: instanceId,
    isRecurringTemplate: undefined,
    recurringTemplateId: templateId,
    occurrenceKey,
    status: ObjectStatus.Unfinished,
    statusAt: undefined,
    instanceOutcome: undefined,
    createdAt: now,
    updatedAt: now,
  })

  // Update instance count
  await db.recurrence.update(templateId, {
    instanceCount: nextSeq,
    lastGeneratedAt: now,
  })
}

/**
 * Reset flexible period — called at period boundary (background or on first access).
 * Generates the first instance of the new period.
 */
export async function resetFlexiblePeriod(templateId: string): Promise<OpResult> {
  const db = getDb()
  const now = getNow()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence) return fail("Recurrence not found")
  if (!recurrence.flexibleCount) return fail("Not a flexible recurrence")

  // Update streak: did they meet the goal last period?
  const metGoal = (recurrence.flexibleCompletedThisPeriod ?? 0) >= recurrence.flexibleCount
  if (metGoal) {
    const newStreak = (recurrence.currentStreak ?? 0) + 1
    await db.recurrence.update(templateId, {
      currentStreak: newStreak,
      longestStreak: Math.max(newStreak, recurrence.longestStreak ?? 0),
    })
  } else if ((recurrence.flexibleCompletedThisPeriod ?? 0) > 0) {
    // Partial completion — don't break streak (grace)
  } else {
    // Zero completions — reset streak
    await db.recurrence.update(templateId, { currentStreak: 0 })
  }

  // Reset counter + start new period
  await db.recurrence.update(templateId, {
    flexibleCompletedThisPeriod: 0,
    flexiblePeriodStart: now,
    updatedAt: now,
  })

  // Generate first instance of new period
  await generateFlexibleInstance(templateId)

  return ok()
}

/**
 * Check if the current flexible period has expired.
 */
function isFlexiblePeriodExpired(recurrence: DbRecurrence): boolean {
  if (!recurrence.flexiblePeriodStart || !recurrence.flexiblePeriod) return true

  const periodStart = dayjs.utc(Number(recurrence.flexiblePeriodStart))
  const now = dayjs.utc()

  if (recurrence.flexiblePeriod === "week") {
    return now.diff(periodStart, "day") >= 7
  }
  if (recurrence.flexiblePeriod === "month") {
    return now.diff(periodStart, "month") >= 1
  }
  return false
}

/**
 * Get flexible progress for a template: { completed, goal, periodLabel }
 */
export async function getFlexibleProgress(templateId: string): Promise<{
  completed: number
  goal: number
  period: "week" | "month"
} | null> {
  const db = getDb()
  const recurrence = await db.recurrence.get(templateId)
  if (!recurrence?.flexibleCount || !recurrence.flexiblePeriod) return null

  // Auto-reset if period expired
  if (isFlexiblePeriodExpired(recurrence)) {
    await resetFlexiblePeriod(templateId)
    return {
      completed: 0,
      goal: recurrence.flexibleCount,
      period: recurrence.flexiblePeriod,
    }
  }

  return {
    completed: recurrence.flexibleCompletedThisPeriod ?? 0,
    goal: recurrence.flexibleCount,
    period: recurrence.flexiblePeriod,
  }
}

// ---------------------------------------------------------------------------
// Recurrence Logbook
// ---------------------------------------------------------------------------

export interface RecurrenceLogbookGroup {
  templateId: string
  templateTitle: string
  recurrence: DbRecurrence
  instances: Array<{
    id: string
    title: string
    occurrenceKey: string
    status: ObjectStatus
    statusAt?: string
    userStatus?: number | string
  }>
}

/**
 * Query completed/skipped instances grouped by their recurring template.
 * Used in Logbook to show recurring task history with streaks.
 *
 * @param limit Max instances per group (default: 30)
 */
export async function queryRecurrenceLogbook(limit = 30): Promise<RecurrenceLogbookGroup[]> {
  const db = getDb()

  // Get all active recurrences
  const recurrences = await db.recurrence
    .filter(r => !r.isDeleted)
    .toArray()

  if (recurrences.length === 0) return []

  const groups: RecurrenceLogbookGroup[] = []

  for (const rec of recurrences) {
    // Get template task title
    const templateTask = await db.totodo.get(rec.id)
    if (!templateTask) continue

    // Get completed/canceled instances
    const instances = await db.totodo
      .where("recurringTemplateId")
      .equals(rec.id)
      .filter(t =>
        !t.isDeleted &&
        (t.status === ObjectStatus.Completed || t.status === ObjectStatus.Canceled),
      )
      .toArray()

    // Sort by occurrenceKey descending (most recent first)
    instances.sort((a, b) => (b.occurrenceKey ?? "").localeCompare(a.occurrenceKey ?? ""))

    // Limit
    const limited = instances.slice(0, limit)

    if (limited.length === 0) continue

    groups.push({
      templateId: rec.id,
      templateTitle: templateTask.title,
      recurrence: rec,
      instances: limited.map(i => ({
        id: i.id!,
        title: i.title,
        occurrenceKey: i.occurrenceKey ?? "",
        status: i.status,
        statusAt: i.statusAt,
        userStatus: i.userStatus,
      })),
    })
  }

  return groups
}
